import React, { useEffect, useMemo, useRef, useState } from "react";
import { ImagePlus, Link2, Trash2 } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { listTeamMembers } from "../../utils/cmsAdminApi";
import { usePlan } from "../../contexts/PlanContext";
import {
  AppButton,
  Callout,
  Checkbox,
  FormField,
  Input,
  NativeSelect,
  Textarea,
  UserAvatar,
} from "../ui";

const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp";

const isActive = (i) => i.active !== false && i.status !== "Inactive";

// The picker holds two kinds of profile, so an option's value carries which
// kind it is. Two kinds of id in one picker with no tag would be a bare
// 24-character string whose meaning the form could only guess at.
const KEY_FOR = { instructor: (id) => `instructor:${id}`, team: (id) => `team:${id}` };
const parseKey = (key) => {
  const [kind, id] = String(key || "").split(":");
  return id ? { kind, id } : { kind: "", id: "" };
};

/**
 * CourseInstructorField — the instructor card on a course's website page
 * (`Course.cms.trainer`).
 *
 * The card is either typed by hand or linked to a profile — either an
 * instructor (someone with an LMS account) or a website team profile
 * (Website → Team: a mentor who teaches this course and has no login). A
 * linked profile fills every field left blank, and is read by the public API
 * on each request — so editing the profile updates the course page without
 * re-saving the course. A field typed here replaces the profile's value on
 * this course's page only; the placeholders show what would appear otherwise.
 *
 * Only one link is kept: picking a team profile clears the instructor one and
 * the other way round, which the server enforces too
 * (courseController.normaliseTrainerLink).
 *
 * The photo is uploaded when picked and saved with the course as a storage key
 * (`photoKey`), never a link: a presigned link would stop working on the page.
 *
 * Props:
 *   courseId        — the saved course, for the photo's storage path (optional)
 *   show            — Course.cms.showTrainerPublicly
 *   onShowChange    — (boolean)
 *   trainer         — Course.cms.trainer as the form holds it
 *   onTrainerChange — (patch) merged into it
 */
export default function CourseInstructorField({ courseId, show, onShowChange, trainer = {}, onTrainerChange }) {
  const { hasPlanFeature } = usePlan();
  const hasWebsite = hasPlanFeature("cmsWebsite");
  const [instructors, setInstructors] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [listState, setListState] = useState("loading"); // loading | ready | unavailable
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const photoInputRef = useRef(null);
  // A photo uploaded on this page and then replaced or removed is deleted,
  // not left behind in storage. The one the course was saved with is not.
  const sessionPhotoKeys = useRef(new Set());

  useEffect(() => {
    let alive = true;
    apiClient
      .get("/instructors/list")
      .then(({ data }) => {
        if (!alive) return;
        setInstructors(Array.isArray(data?.instructors) ? data.instructors : []);
        setListState("ready");
      })
      .catch(() => alive && setListState("unavailable"));
    return () => {
      alive = false;
    };
  }, []);

  // Website team profiles. Behind the website plan, and a failure here only
  // costs the team options — the instructor list is what this field has
  // always offered, so it must not be held up by them.
  useEffect(() => {
    if (!hasWebsite) return undefined;
    let alive = true;
    listTeamMembers()
      .then((res) => alive && setTeamMembers(Array.isArray(res?.data) ? res.data : []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [hasWebsite]);

  const linkedInstructorId = trainer.instructorId ? String(trainer.instructorId) : "";
  const linkedTeamId = trainer.teamMemberId ? String(trainer.teamMemberId) : "";
  const selectedKey = linkedTeamId
    ? KEY_FOR.team(linkedTeamId)
    : linkedInstructorId
      ? KEY_FOR.instructor(linkedInstructorId)
      : "";

  const linkedMember = useMemo(
    () => teamMembers.find((m) => String(m._id) === linkedTeamId) || null,
    [teamMembers, linkedTeamId]
  );
  const linkedInstructor = useMemo(
    () => instructors.find((i) => String(i._id) === linkedInstructorId) || null,
    [instructors, linkedInstructorId]
  );

  // One shape for whichever kind is linked, so the rest of the field does not
  // branch on it. `avatar` is what an instructor row calls its photo.
  const linked = useMemo(() => {
    if (linkedMember) {
      return {
        name: linkedMember.name,
        subject: linkedMember.designation,
        qualification: "",
        about: linkedMember.bio,
        avatar: linkedMember.photoUrl || "",
        kind: "team",
      };
    }
    if (linkedInstructor) {
      return { ...linkedInstructor, kind: "instructor" };
    }
    return null;
  }, [linkedMember, linkedInstructor]);

  const linkBroken =
    (Boolean(linkedInstructorId) && listState === "ready" && (!linkedInstructor || !isActive(linkedInstructor)))
    || (Boolean(linkedTeamId) && hasWebsite && teamMembers.length > 0 && !linkedMember);

  const instructorOptions = useMemo(
    () => instructors
      .filter((i) => isActive(i) || String(i._id) === linkedInstructorId)
      .map((i) => ({
        value: KEY_FOR.instructor(String(i._id)),
        label: [i.name || i.email || "Unnamed instructor", i.subject].filter(Boolean).join(" — "),
      })),
    [instructors, linkedInstructorId]
  );

  const teamOptions = useMemo(
    () => teamMembers.map((m) => ({
      value: KEY_FOR.team(String(m._id)),
      label: [m.name, m.designation].filter(Boolean).join(" — "),
    })),
    [teamMembers]
  );

  const pickProfile = (key) => {
    const { kind, id } = parseKey(key);
    onTrainerChange({
      instructorId: kind === "instructor" ? id : null,
      teamMemberId: kind === "team" ? id : null,
    });
  };

  const discardSessionPhoto = (key) => {
    if (!key || !sessionPhotoKeys.current.has(key)) return;
    sessionPhotoKeys.current.delete(key);
    apiClient.delete("/files/delete", { data: { key } }).catch(() => {});
  };

  const handlePhotoPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setPhotoError("Choose a JPG, PNG or WebP image.");
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      setPhotoError("The photo must be smaller than 5 MB.");
      return;
    }
    setPhotoError("");
    setPhotoUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("entity", "courses");
      body.append("entityId", courseId || "new");
      body.append("type", "trainer-photo");
      const { data: uploaded } = await apiClient.post("/files/upload", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data: read } = await apiClient.get(
        `/files/presign-get?key=${encodeURIComponent(uploaded.key)}`
      );
      discardSessionPhoto(trainer.photoKey);
      sessionPhotoKeys.current.add(uploaded.key);
      onTrainerChange({ photoKey: uploaded.key, photoPreviewUrl: read.url, photoUrl: "" });
    } catch (err) {
      setPhotoError(err?.response?.data?.error || "Could not upload the photo.");
    } finally {
      setPhotoUploading(false);
    }
  };

  const removePhoto = () => {
    discardSessionPhoto(trainer.photoKey);
    onTrainerChange({ photoKey: "", photoPreviewUrl: "", photoUrl: "" });
  };

  const ownPhoto = trainer.photoPreviewUrl || trainer.photoUrl || "";
  const profilePhoto = linked?.avatar || "";
  const shownPhoto = ownPhoto || profilePhoto;
  const shownName = (trainer.name || "").trim() || linked?.name || "";

  const status = !show
    ? "Hidden. Nothing about the instructor is sent to your website; what is set below is kept for when you turn it back on."
    : shownName
      ? "Shown after the syllabus on this course's website page."
      : "Link a profile or add a name below. The card appears on your website once it has a name.";

  return (
    <div className="tw-flex tw-flex-col tw-gap-4 tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-alt tw-p-4">
      <Checkbox
        checked={show}
        onChange={(e) => onShowChange(e.target.checked)}
        label="Show instructor details on the website course page"
        description={status}
      />

      {show && (
        <>
          <FormField
            label="Who teaches this course"
            hint={
              listState === "unavailable"
                ? "Your role cannot list instructors, so the details below have to be typed."
                : hasWebsite
                  ? "Link an instructor, or a website profile from Website → Team for a mentor with no LMS login. Their name, photo, role and bio fill the card, and profile edits show up here automatically."
                  : "Link one of your instructors to use their name, photo, role and bio. Profile edits show up here automatically."
            }
          >
            <NativeSelect
              size="lg"
              value={selectedKey}
              disabled={listState !== "ready"}
              onChange={(e) => pickProfile(e.target.value)}
            >
              {listState === "loading" ? (
                <option value={selectedKey}>Loading…</option>
              ) : (
                <>
                  <option value="">Not linked — type the details below</option>
                  {teamOptions.length > 0 && (
                    <optgroup label="Website team (no LMS account)">
                      {teamOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </optgroup>
                  )}
                  {instructorOptions.length > 0 && (
                    <optgroup label="Instructors (LMS accounts)">
                      {instructorOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </optgroup>
                  )}
                  {/* A link to a profile that is gone still has to be the
                      selected option, or the select would silently show
                      "Not linked" for a course that is linked. */}
                  {selectedKey
                    && !teamOptions.some((o) => o.value === selectedKey)
                    && !instructorOptions.some((o) => o.value === selectedKey) && (
                    <option value={selectedKey}>Linked profile (no longer available)</option>
                  )}
                </>
              )}
            </NativeSelect>
          </FormField>

          {linkBroken && (
            <Callout tone="warning">
              The linked profile was removed or deactivated, so the website shows only the details
              typed below. Link another one or unlink it.
            </Callout>
          )}

          {linked && !linkBroken && (
            <Callout tone="info" icon={<Link2 size={14} aria-hidden="true" />}>
              Using {linked.name}&apos;s{' '}
              {linked.kind === "team" ? "website profile" : "instructor profile"}. Anything you fill
              in below replaces it on this course&apos;s page only — leave a field empty to keep the
              profile&apos;s version.
            </Callout>
          )}

          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
            <UserAvatar name={shownName || "Instructor"} src={shownPhoto || undefined} size="xl" bordered />
            <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-2">
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                <AppButton
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={photoUploading}
                  onClick={() => photoInputRef.current?.click()}
                >
                  <ImagePlus size={16} />
                  {photoUploading ? "Uploading..." : ownPhoto ? "Change photo" : "Upload photo"}
                </AppButton>
                {ownPhoto && !photoUploading && (
                  <AppButton type="button" variant="ghost-destructive" size="sm" onClick={removePhoto}>
                    <Trash2 size={16} /> Remove
                  </AppButton>
                )}
              </div>
              <span className="tw-text-xs tw-text-content-subtle">
                {ownPhoto
                  ? "Photo for this course's page."
                  : profilePhoto
                    ? "Showing the photo from their profile."
                    : "JPG, PNG or WebP, up to 5 MB. Without one, the page shows their initials."}
              </span>
            </div>
            <input
              ref={photoInputRef}
              type="file"
              accept={PHOTO_ACCEPT}
              onChange={handlePhotoPick}
              className="tw-hidden"
              tabIndex={-1}
              aria-hidden="true"
            />
          </div>
          {photoError && <Callout tone="danger" role="alert">{photoError}</Callout>}

          <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
            <FormField label="Name">
              <Input
                size="lg"
                value={trainer.name || ""}
                onChange={(e) => onTrainerChange({ name: e.target.value })}
                placeholder={linked?.name || "e.g. Ananya Verma"}
              />
            </FormField>
            <FormField label="Role / title" hint="The short line under their name.">
              <Input
                size="lg"
                value={trainer.title || ""}
                onChange={(e) => onTrainerChange({ title: e.target.value })}
                placeholder={linked?.subject || linked?.qualification || "e.g. Deep Learning Specialist & ML Engineer"}
              />
            </FormField>
            <FormField label="Students trained" hint="Optional. Leave blank or 0 to hide it.">
              <Input
                size="lg"
                type="number"
                min={0}
                value={trainer.studentsTrained ?? ""}
                onChange={(e) => onTrainerChange({ studentsTrained: e.target.value })}
                placeholder="e.g. 12000"
              />
            </FormField>
            <FormField label="Courses led" hint="Optional. Leave blank or 0 to hide it.">
              <Input
                size="lg"
                type="number"
                min={0}
                value={trainer.coursesLed ?? ""}
                onChange={(e) => onTrainerChange({ coursesLed: e.target.value })}
                placeholder="e.g. 6"
              />
            </FormField>
            <FormField
              label="Bio"
              className="sm:tw-col-span-2"
              hint="Leave a blank line between paragraphs to break them up."
            >
              <Textarea
                size="lg"
                rows={4}
                value={trainer.bio || ""}
                onChange={(e) => onTrainerChange({ bio: e.target.value })}
                placeholder={linked?.about || "A short introduction for prospective students…"}
              />
            </FormField>
          </div>
        </>
      )}
    </div>
  );
}
